/* eslint-disable @typescript-eslint/no-unused-vars */
import { ApiCategory } from 'idt-ecommerce-api/_api/api-catalog/api-categories/api-categories.models';
import { ApiProduct } from 'idt-ecommerce-api/_api/api-catalog/api-products/api-products.models';
import { ApiPhysicalStoreOpeningHours } from 'idt-ecommerce-api/_api/api-catalog/api-stores/api-stores.models';
import { Category, CategoryAnalytics } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { MediaClazz } from 'idt-ecommerce-api/catalog/products/medias/medias.models';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { PhysicalStoreSchedule } from 'idt-ecommerce-api/stores/stores.models';
import { MetaTag } from 'shared/models/meta-tags.models';

import { EcommerceBrandSettings } from './config.models';


export const EcommerceBrandSettingsOysho: EcommerceBrandSettings = {

  // --- Constants ---
  brandId: '6',
  brandName: 'Oysho',
  mediaPathSuffix: '1_1_3',
  // --- Functions ---

  // Ref: https://axinic.central.inditex.grp/jira/browse/MECFRONT-749
  reorderApiCategories(apiCategories: ApiCategory[]): ApiCategory[] {
    let apiCategoryTree: ApiCategory[] = [];

    const oyshoSportIndex = apiCategories.findIndex((category) => isDefined(category.key) && category.key.includes('ROOT_NAV'));

    if (oyshoSportIndex >= 0) {
      const oyshoSportCategory = apiCategories[oyshoSportIndex];
      const categoriesWithoutSportCategory = [...apiCategories.slice(0, oyshoSportIndex), ...apiCategories.slice(oyshoSportIndex + 1)];

      const oyshoCategory: ApiCategory = {
        id: 0,
        name: 'OYSHO',
        nameEn: 'OYSHO',
        shortDescription: 'Oysho root fake cms category',
        key: 'OYSHO_FAKE_NOSEO',
        type: '2',
        viewCategoryId: 0,
        subcategories: categoriesWithoutSportCategory,
        attachments: [],
        categoryUrl: 'femme-limited-edition-n1067'
      };

      apiCategoryTree = [oyshoCategory, oyshoSportCategory];
    } else {
      apiCategoryTree = apiCategories;
    }

    return apiCategoryTree;
  },

  schedulesFromApi(openingHours?: ApiPhysicalStoreOpeningHours, context?: EcommerceContext): PhysicalStoreSchedule[] {
    const translations = context?.store?.details?.translations;
    if (!isDefined(context) || !isDefined(openingHours) || !isDefined(translations)) { return []; }

    const hoursString = translations['hours-short'];

    return openingHours.schedule.map((period) => {
      const hours = context.currentLanguage.direction === 'ltr'
        ? period.timeStripList.map((time) => `${time.initHour + hoursString} - ${time.endHour + hoursString}`)
        : period.timeStripList.map((time) => `${hoursString + time.initHour} - ${hoursString + time.endHour}`);

      if (period.weekdays.length === 1) {
        return {
          hours,
          days: period.weekdays,
          daysText: translations[`weekday-${period.weekdays[0]}`]
        };
      }

      return {
        hours,
        days: period.weekdays,
        daysText: `${translations[`weekday-${period.weekdays[0]}`]} - ${translations[`weekday-${period.weekdays[period.weekdays.length - 1]}`]}`
      };
    });
  },

  // Ref:  https://jira.inditex.com/jira/browse/MECFRONT-1073
  transformProductWithMocacos(productDetail: ApiProduct, mocacos: ApiProduct[]): ApiProduct {
    const productMainColor = productDetail.mainColorid;

    const colorSelectedMocaco = mocacos.find((mocaco: ApiProduct) => {
      return mocaco.mainColorid === productMainColor && mocaco?.detail?.relatedProducts && mocaco?.detail?.relatedProducts.length > 0;
    });

    if (colorSelectedMocaco?.detail?.relatedProducts) {
      productDetail.detail.relatedProducts = colorSelectedMocaco.detail.relatedProducts;
    }
    return productDetail;
  },

  // Ref: https://axinic.central.inditex.grp/confluence/display/ECOMNEG/Oysho+-+Proyecto+XMEDIA
  parseMediaClazzOverride(apiClazz: number) {
    let mediaClazz: MediaClazz;
    switch (apiClazz) {
      case 1:
        mediaClazz = { id: apiClazz, type: 'main' };
        break;
      case 2: case 21: case 22: case 23: case 24: case 25: case 26: case 27: case 28: case 29:
        mediaClazz = { id: apiClazz, type: 'secondary' };
        break;
      case 3:
        mediaClazz = { id: apiClazz, type: 'colorcut' };
        break;
      case 4: case 5:
        mediaClazz = { id: apiClazz, type: 'silhouette' };
        break;
      case 6:
        mediaClazz = { id: apiClazz, type: 'alternative' };
        break;
      case 7: case 8:
        mediaClazz = { id: apiClazz, type: 'double' };
        break;
      case 12:
        mediaClazz = { id: apiClazz, type: 'video' };
        break;
      default:
        mediaClazz = { id: apiClazz, type: 'unknown' };
        break;
    }
    return mediaClazz;
  },

  setCookies(document: Document) {
    const script: HTMLScriptElement = document.createElement('script');
    script.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
    script.setAttribute('type', 'text/javascript');
    script.setAttribute('charset', 'UTF-8');
    script.setAttribute('data-document-language', 'true');
    script.setAttribute('data-domain-script', '41f2def1-0b03-4bfa-921c-42c83ca13fe7');
    document.body.appendChild(script);
  },

  obtainParsedSEOText(key: string, context: EcommerceContext, product?: Product, category?: CategoryAnalytics) {
    const translations = context.store?.details?.translations;
    const wildcardRE = /{[0-9]+}/gi;
    let translatedText;
    const id = product?.id || category?.id;
    if (translations) {
      translatedText = translations[key];
      if (isDefined(id)) {
        const keyParts = key.split('_');
        keyParts[1] = `${id}-${keyParts[1]}`;
        const idKey = keyParts.join('_');
        translatedText = translations[idKey] ? translations[idKey] : translatedText;
      }
    }
    const parsedText = (translatedText || key).replace(wildcardRE, (wildcard) => {
      const wildcardHelper = BrandWildcards[`getWildcard${wildcard.replace(/{|}/g, '')}`];
      let parsedWildcard = '';
      if (isDefined(wildcardHelper)) {
        parsedWildcard = wildcardHelper(context, product, category);
      }
      return parsedWildcard;
    });
    return parsedText || key;
  },

  obtainDefaultMetaTags(pageType: string): MetaTag[] {
    const pageTypesMap = new Map([
      ['grid', 'PRODUCT-GRID'],
      ['product', 'PRODUCT-DETAIL'],
      ['home', 'HOME']
    ]);
    const traductionPageKey = pageTypesMap.get(pageType);
    if (!isDefined(traductionPageKey)) {
      return [];
    }
    return [
      { name: '', property: 'og:title', content: `${traductionPageKey}_TAB-TITLE`, default: true },
      { name: '', property: 'og:type', content: 'website', default: true },
      { name: '', property: 'og:image', content: '{28}', default: true },
      { name: '', property: 'og:url', content: '{29}', default: true },
      { name: '', property: 'og:description', content: `${traductionPageKey}_META-DESCRIPTION`, default: true },
      { name: '', property: 'twitter:card', content: 'summary', default: true },
      { name: '', property: 'twitter:site', content: '@Oysho', default: true },
      { name: '', property: 'twitter:title', content: `${traductionPageKey}_TAB-TITLE`, default: true },
      { name: '', property: 'twitter:description', content: `${traductionPageKey}_META-DESCRIPTION`, default: true },
      { name: '', property: 'twitter:image', content: '{28}', default: true },
      { name: 'description', property: '', content: `${traductionPageKey}_META-DESCRIPTION`, default: true },
      { name: 'keywords', property: '', content: `${traductionPageKey}_KEYWORDS`, default: true }
    ];
  }
};

export const getCategoriesTree = (category: CategoryAnalytics) => {
  const numCategories = category?.sectionPathEn?.split('/').length || 0;
  let path = category?.sectionPath;
  for (let i: number = 0; i < numCategories - 1; i++) {
    path = decodeURIComponent(path);
  }
  return path.replace(/-/gi, ' ').split('/')
    .map(pathCategory => pathCategory[0].toUpperCase() + pathCategory.slice(1));
};

export const BrandWildcards: Record<string, (context: EcommerceContext, product?: Product, category?: CategoryAnalytics) => string> = {

  /** Nombre del páis de la tienda (España) */
  getWildcard0(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return context.store.countryName;
  },

  /** Nombre de la categoría */
  getWildcard1(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return category?.name || '';
  },

  /** Ruta a la categoría en sentido descendente */
  getWildcard2(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let categoryPath;
    if (isDefined(category)) {
      const categories = getCategoriesTree(category);
      categoryPath = categories.join(' - ');
    }
    return categoryPath || '';
  },

  /** Ruta a la categoría en sentido ascendente */
  getWildcard3(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let categoryPath;
    if (isDefined(category)) {
      const categories = getCategoriesTree(category).reverse();
      categoryPath = categories.join(' - ');
    }
    return categoryPath || '';
  },

  /** Nombre del producto */
  getWildcard4(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.name || '';
  },

  /** Precio del producto */
  getWildcard5(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    const price = product?.priceInfo?.priceWithCurrencySymbol?.toString();
    const currencySymbol = context?.store.details.currencyInfo?.currencySymbol;
    let formattedPrice = price?.replace(/\s/g, '').replace(',', '.');
    if (isDefined(currencySymbol) && isDefined(formattedPrice)) {
      formattedPrice = formattedPrice.replace(currencySymbol, '');
    }
    return formattedPrice || '';
  },

  /** Nombre corto de la tienda */
  getWildcard7(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return context.store.countryCode;
  },

  /** Descripción del producto */
  getWildcard8(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.longDescription || '';
  },

  /** Sección de la categoría */
  getWildcard9(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return 'Women';
  },

  /** Temporada del producto */
  getWildcard10(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.fullReference?.split('-')[1] || '';
  },

  /** Año del producto */
  getWildcard11(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    const season = product?.fullReference?.split('-')[1];
    const year = season?.match(/\d/g)?.join('');
    return year || '';
  },

  /** Sección del producto */
  getWildcar12(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.productType || '';
  },

  /** Nombre de la categoría padre */
  getWildcard13(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let parentCategory;
    if (isDefined(category)) {
      const categories = getCategoriesTree(category);
      parentCategory = categories[categories.length - 2];
    }
    return parentCategory || '';
  },

  /** Ruta hasta la categoría en sentido ascendente sin incluir categorías "View all" */
  // TODO: Identificar categorías que son 'Ver todo'
  getWildcard14(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let categoryPath;
    if (isDefined(category)) {
      const categories = getCategoriesTree(category).reverse();
      categoryPath = categories.join(' - ');
    }
    return categoryPath || '';
  },

  /** Descripción del producto */
  getWildcard15(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.longDescription || '';
  },

  /** Símbolo de la moneda de la tienda actual */
  getWildcard16(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return context?.store.details.currencyInfo?.currencySymbol || '';
  },

  /** Familia del producto */
  getWildcard17(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.familyName || '';
  },

  /** Referencia del producto */
  getWildcard18(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return product?.fullReference || '';
  },

  // TODO: Añadir metas y comodines para la página de tienda

  /** Nombre de la tienda */
  getWildcard19(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** Dirección de la tienda */
  getWildcard20(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** Ciudad de la tienda */
  getWildcard21(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** País de la tienda física */
  getWildcard22(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** Teléfono de la tienda física */
  getWildcard23(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** Título de SEO de la categoría a la que pertenece el producto */
  getWildcard24(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let categoryTitle;
    if (EcommerceBrandSettingsOysho.obtainParsedSEOText) {
      categoryTitle = EcommerceBrandSettingsOysho.obtainParsedSEOText('PRODUCT-GRID_TAB-TITLE', context, product, category);
    }
    return categoryTitle || '';
  },

  /** Término de búsqueda */
  getWildcard25(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** Año actual */
  getWildcard26(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return new Date().getFullYear().toString() || '';
  },

  /** Campaña actual basada en la fecha */
  getWildcard27(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    return '';
  },

  /** Imagen principal de la página (en caso de ser parrilla o home será el logo) */
  getWildcard28(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let imageUrl = 'https://static.oysho.net/6/static2/itxwebstandard/logo/logo.png';
    if (isDefined(product)) {
      const mainColor = product.colors[0];
      if (isDefined(mainColor)) {
        const images = ProductsHelpers.imagesForColor(mainColor, 'product_page');
        imageUrl = images[0]?.path;
      }
    }
    return imageUrl;
  },

  /** Url de la página actual */
  getWildcard29(context: EcommerceContext, product?: Product, category?: CategoryAnalytics): string {
    let url = 'https://www.oysho.com';
    if (isDefined(category)) {
      url = category.urlWeb;
    }
    if (isDefined(product)) {
      url = product.urlWeb;
    }
    return url;
  },
};
